import { useSelector } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { array, record } from 'fp-ts';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { upperFirst } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { EnterpriseProfilePrivilegesEnumNames } from '@mews-commander/legacy-data-structures';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getCurrentScopePrivileges } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useLocalizePrivilege, usePrivilegesFilter } from '../../../roles/PrivilegesTable/utils';
import { createListItems, FormOption, orderByLabel } from '../../utils';
import { FieldNames } from '../FieldNames';

export const PrivilegesField = ({ disabled }: { disabled: boolean }) => {
    const privilegesOptions = usePrivilegesOptions();

    return (
        <FormSelectField
            name={FieldNames.Privileges}
            label={<LocalizedText textKey={TextKey.SelectPermissions} />}
            disabled={disabled}
            multiselect
            required
        >
            {createListItems(privilegesOptions)}
        </FormSelectField>
    );
};

const usePrivilegesOptions = (): FormOption[] => {
    const privilegesEnumValues = usePrivilegesFilter({});
    const currentScopePrivileges: Record<string, boolean> = useSelector(getCurrentScopePrivileges);
    const canAccessAllEnterprises = useSelector(hasPlatformScopeWithAccessAllEnterprises);
    const localizePrivilege = useLocalizePrivilege();

    const disabledPrivileges = pipe(
        currentScopePrivileges,
        record.filter((value) => value === false),
        record.keys,
        array.map((key: string) => upperFirst(key) as EnterpriseProfilePrivilegesEnumNames)
    );

    return pipe(
        privilegesEnumValues,
        array.map(({ value }) => value),
        array.map((privilege) => ({
            key: privilege,
            value: privilege,
            label: localizePrivilege(privilege).label,
            disabled: !canAccessAllEnterprises && disabledPrivileges.includes(privilege),
        })),
        array.sort(orderByLabel)
    );
};
